import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";
import { SiteHeader } from "../../components/site/SiteHeader";
import { SiteFooter } from "../../components/site/SiteFooter";
import { PageHero } from "../../components/site/PageHero";
import { CTABand } from "../../components/site/CTABand";
import { Reveal, Stagger, StaggerItem } from "../../components/motion/Reveal";
import { CASE_STUDIES } from "../../data/caseStudies";
import { PROJECT_CATEGORIES } from "../../data/projects";

const ProjectCard = ({ p, category, image, testid }) => (
  <div className="group flex flex-col h-full border border-white/10 bg-white/[0.02] hover:border-nx-gold/40 transition-colors duration-300" data-testid={testid}>
    <div className="relative overflow-hidden">
      <img src={image} alt={p.title} className="w-full h-48 object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
      <span className="absolute top-4 left-4 text-[10px] tracking-[0.22em] uppercase bg-nx-navy/85 backdrop-blur-sm text-nx-goldlight border border-nx-gold/40 px-3 py-1.5">{category}</span>
    </div>
    <div className="flex flex-col flex-1 p-6">
      <h3 className="font-marcellus text-lg text-white leading-snug">{p.title}</h3>
      <p className="mt-2.5 flex items-center gap-1.5 text-[11px] tracking-[0.16em] uppercase text-nx-goldlight"><MapPin size={12} /> {p.location}</p>
      <p className="text-sm text-slate-400 mt-3 leading-relaxed flex-1">{p.description}</p>
    </div>
  </div>
);

export default function Projects() {
  return (
    <div className="font-publicsans bg-nx-navy text-slate-200 antialiased" data-testid="projects-page">
      <SiteHeader />
      <PageHero
        crumb="Projects"
        eyebrow="Projects"
        title="Work that speaks"
        titleAccent="through outcomes."
        description="Case studies from our project portfolio — requirement, challenge, design, implementation and result."
      />
      <section className="py-16 sm:py-24 bg-nx-navy2 border-b border-white/10" data-testid="installations-section">
        <div className="nx-wrap">
          <Reveal>
            <div className="flex items-center gap-4 mb-4">
              <span className="h-px w-12 bg-nx-gold/70" />
              <span className="text-[10px] sm:text-xs tracking-[0.16em] sm:tracking-[0.24em] uppercase text-nx-goldlight">Completed Installations</span>
            </div>
            <h2 className="font-marcellus text-3xl sm:text-4xl text-white leading-tight max-w-3xl mb-3">Installations across Kerala, by category.</h2>
            <p className="text-sm text-slate-500 mb-12 max-w-2xl">Illustrative project cards for site review — real project photographs replace these placeholders as client permissions come in.</p>
          </Reveal>
          {PROJECT_CATEGORIES.map((cat) => (
            <div key={cat.slug} className="mb-14 last:mb-0" data-testid={`project-category-${cat.slug}`}>
              <Reveal>
                <h3 className="font-marcellus text-xl sm:text-2xl text-nx-goldlight mb-6">{cat.name}</h3>
              </Reveal>
              <Stagger className="grid sm:grid-cols-2 gap-6">
                {cat.projects.map((p, i) => (
                  <StaggerItem key={p.title}>
                    <ProjectCard p={p} category={cat.name} image={p.image || cat.image} testid={`project-card-${cat.slug}-${i}`} />
                  </StaggerItem>
                ))}
              </Stagger>
            </div>
          ))}
        </div>
      </section>
      <section className="py-16 sm:py-24">
        <div className="nx-wrap">
          <Reveal>
            <div className="border border-nx-gold/30 bg-nx-gold/[0.05] p-6 sm:p-7 mb-12 max-w-3xl" data-testid="projects-note">
              <p className="text-sm text-slate-300 leading-relaxed">
                <span className="text-nx-goldlight font-medium">Note:</span> These case studies are representative drafts prepared for site review — client names and verified figures are added only with each client&apos;s written permission before publication.
              </p>
            </div>
          </Reveal>
          <Stagger className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {CASE_STUDIES.map((c, i) => (
              <StaggerItem key={c.slug}>
                <Link to={`/projects/${c.slug}`} className="group flex flex-col h-full border border-white/10 bg-white/[0.02] hover:border-nx-gold/40 transition-colors duration-300" data-testid={`project-card-${c.slug}`}>
                  <div className="relative overflow-hidden">
                    <img src={c.image} alt={c.title} className="w-full h-52 object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                    <span className="absolute top-4 left-4 text-[10px] tracking-[0.22em] uppercase bg-nx-navy/85 backdrop-blur-sm text-nx-goldlight border border-nx-gold/40 px-3 py-1.5">{c.category}</span>
                  </div>
                  <div className="flex flex-col flex-1 p-7">
                    <div className="flex items-center justify-between text-[11px] tracking-[0.18em] uppercase text-slate-500 mb-4">
                      <span>{c.location}</span>
                      <span>{String(i + 1).padStart(2, "0")}</span>
                    </div>
                    <h2 className="font-marcellus text-xl text-white leading-snug">{c.title}</h2>
                    <p className="text-sm text-slate-400 mt-3 leading-relaxed flex-1">{c.summary}</p>
                    <span className="mt-6 inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-nx-goldlight">
                      Read case study <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
      <CTABand title="Your project could be here next." />
      <SiteFooter />
    </div>
  );
}
